// src/encounter/intelligence/ask-panel.tsx
//
// The Ask card (Samer, 4 Nov 2026: "If I ask 'What's the patient's age?' will it answer?"). The clinician's free
// question goes to the state interpreter (encounter owner, 1 Nov: "state-interpreter stays for the free Ask question
// only") through POST /api/intelligence/run with the clinician's own token (useIntelligenceRun). The engine plans, calls
// its tools on the record, and delivers the answer only after the rule check and the AI checks pass; otherwise the card
// shows the catalog's fixed state line, never the engine's text. Labelled "AI draft · review before use"; the trace
// (agent, model, tools with item counts, each check's verdict) is under "How this was produced". It acts on nothing.
// A new question (request.seq) replaces the answer; a patient change clears it (the panel is keyed on the patient).
import { useEffect, useId, useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { useIntelligenceRun } from '../intelligence-engine';
import { C } from './explain-card-classes';
import { answerBlocks } from './ask-answer';
import { ASK_AGENT, ASK_STATE_LINE, ASK_WORDS, CHECK_NAME, WITHHELD_PLAIN, type AskRequest } from './ask-words';
import { cardStateOf, criticalRowsOf, traceOf } from './run-card-state';
import { WORDS } from './explanation';

export function AskPanel({ patientId, request }: { patientId: string; request: AskRequest | null }) {
  const run = useIntelligenceRun();
  const uid = useId();
  // The trace is open for one question only: a new question closes it (no state reset inside the effect).
  const [openSeq, setOpenSeq] = useState(0);
  const { start } = run;
  const seq = request?.seq ?? 0;
  const question = request?.question ?? '';

  useEffect(() => {
    if (!seq || !question) return;
    void start({ ...ASK_AGENT, question, patientId, origin: { tab: 'encounter', question } });
  }, [seq, question, patientId, start]);

  if (!request) return null;
  const state = cardStateOf(run);
  const trace = traceOf(run);
  // Withheld: the engine's plain reason when it is one of the fixed sentences, else the catalog line.
  const plain = [...run.events].reverse().find((e) => e.type === 'error')?.plain;
  const line = state === 'withheld' && typeof plain === 'string' && WITHHELD_PLAIN.includes(plain) ? plain : ASK_STATE_LINE[state];
  const open = openSeq === seq;
  // X5 (Codex, 4 Nov): a failed run still shows the record's critical rows from the rule path, never the model's text.
  const critical = state === 'answered' ? [] : criticalRowsOf(run);

  return (
    <section className={C.card} aria-labelledby={`${uid}-title`} data-slot="ask-card" data-state={state}>
      <div className={C.titleRow}>
        <h4 id={`${uid}-title`} className={C.title}>{ASK_WORDS.title}</h4>
      </div>
      <p className={C.stateLine} data-slot="ask-question">{request.question}</p>
      <p className={C.meta} data-slot="ask-origin">
        {ASK_WORDS.origin} ·{' '}
        <button type="button" className={C.linkButton} aria-expanded={open} aria-controls={open ? `${uid}-how` : undefined} onClick={() => setOpenSeq(open ? 0 : seq)} disabled={!trace.length}>
          {ASK_WORDS.how}
        </button>
      </p>
      {open && trace.length ? (
        <ul id={`${uid}-how`} className={C.details} data-slot="ask-trace">
          {trace.map((t, i) => (
            <li key={i}>
              {t.kind === 'agent' ? `Agent: ${t.agent}${t.model ? ` (${t.model})` : ''}` : t.kind === 'model-turn' ? `Model turn ${t.turn}` : t.kind === 'tool' ? `Read: ${t.name}${t.count !== null ? ` (${t.count} items)` : ''}` : `${CHECK_NAME[t.check]}: ${t.verdict}`}
            </li>
          ))}
        </ul>
      ) : null}
      {state === 'loading' ? (
        <div className={C.footerRow} role="status" aria-busy="true" data-slot="ask-loading">
          <span>{ASK_WORDS.loading}</span>
          <Button variant="tertiary" size="sm" className="text-xs font-normal" onClick={() => run.cancel()}>{ASK_WORDS.stop}</Button>
        </div>
      ) : state === 'answered' && run.answer ? (
        <>
          <ul className={C.lines} data-slot="ask-answer">
            {answerBlocks(run.answer).map((b, i) => (
              <li key={i} data-kind={b.kind} data-cites={b.cites.join(' ')} className={b.kind === 'heading' ? 'font-semibold' : b.kind === 'item' ? 'pl-3' : undefined}>
                {b.text}
                {b.cites.length ? <span className={C.meta} aria-label={`Sources: ${b.cites.join(', ')}`}> · {b.cites.length === 1 ? '1 source' : `${b.cites.length} sources`}</span> : null}
              </li>
            ))}
          </ul>
          <p className={C.meta} data-slot="ask-review">{ASK_WORDS.review}</p>
        </>
      ) : line ? (
        <div className={C.footerRow}>
          <p role="status" className={C.stateLine} data-slot="ask-state">{line}</p>
          {state !== 'denied' && state !== 'session-expired' ? (
            <Button variant="tertiary" size="sm" className="text-xs font-normal" data-slot="ask-retry" onClick={() => void start({ ...ASK_AGENT, question, patientId, origin: { tab: 'encounter', question } })}>{ASK_WORDS.retry}</Button>
          ) : null}
        </div>
      ) : null}
      {critical.length ? (
        <ul className={C.lines} data-slot="ask-critical">
          {critical.map((r, i) => (
            <li key={i} data-cites={r.cites.join(' ')}>
              {r.text}
              <span className={C.meta}> · {WORDS.criticalTag}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
